import { type ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';

import { enUS } from '../catalogs/en-US';

import { LocaleContext } from './internal/LocaleContext';
import { defaultLocale, isLocale, type Locale } from './locales';
import { MessagesProvider } from './MessagesProvider';

const storageKey = 'ld:locale';

const catalogs: Record<Locale, () => Promise<Record<string, string>>> = {
  'en-US': () => Promise.resolve<Record<string, string>>(enUS),
  'es-ES': async () => (await import(/* webpackChunkName: "messages-es-ES" */ '../catalogs/es-ES')).esES,
  'ja-JP': async () => (await import(/* webpackChunkName: "messages-ja-JP" */ '../catalogs/ja-JP')).jaJP,
};

function readStoredLocale(): Locale {
  try {
    const stored = localStorage.getItem(storageKey);
    return isLocale(stored) ? stored : defaultLocale;
  } catch {
    return defaultLocale;
  }
}

export function LocaleProvider({
  children,
  canSelectLocale = true,
}: {
  children: ReactNode;
  canSelectLocale?: boolean;
}) {
  const [storedLocale, setLocaleState] = useState<Locale>(readStoredLocale);
  const [catalog, setCatalog] = useState<{ locale: Locale; messages: Record<string, string> }>({
    locale: defaultLocale,
    messages: enUS,
  });
  const locale = canSelectLocale ? storedLocale : defaultLocale;

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      localStorage.setItem(storageKey, next);
    } catch {
      return;
    }
  }, []);

  useEffect(() => {
    if (locale === catalog.locale) {
      return;
    }
    let current = true;
    catalogs[locale]().then(
      (messages) => {
        if (current) {
          setCatalog({ locale, messages });
        }
      },
      () => {
        if (current) {
          setCatalog({ locale: defaultLocale, messages: enUS });
          setLocale(defaultLocale);
        }
      },
    );
    return () => {
      current = false;
    };
  }, [locale, catalog.locale, setLocale]);

  useLayoutEffect(() => {
    document.documentElement.lang = catalog.locale;
  }, [catalog.locale]);

  const value = useMemo(() => ({ locale, setLocale }), [locale, setLocale]);

  return (
    <LocaleContext.Provider value={value}>
      <MessagesProvider key={catalog.locale} locale={catalog.locale} messages={catalog.messages}>
        {children}
      </MessagesProvider>
    </LocaleContext.Provider>
  );
}
